import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Mic, MicOff, Send, Loader2, Check, X, SkipForward, Volume2, VolumeX, RotateCw, Eye, EyeOff, Play, StopCircle, Trophy, Sparkles, Target } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import InterviewUpgradeGate from "@/components/InterviewUpgradeGate";
import InterviewerAvatar from "@/components/InterviewerAvatar";

export default function InterviewSession() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // {is_correct, feedback, accepted_answers}
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  // If the user toggles off audio, we stop auto-speaking new questions
  // but the manual "Speak" button still works when they want to hear it.
  const [muteTTS, setMuteTTS] = useState(() => localStorage.getItem("iv_mute_tts") === "1");
  const [avatarState, setAvatarState] = useState("idle");
  const [currentAudioEl, setCurrentAudioEl] = useState(null);
  const [hideQuestion, setHideQuestion] = useState(searchParams.get("hide") === "1");
  const speed = parseFloat(searchParams.get("speed") || "1") || 1;
  const voice = searchParams.get("voice") || "nova";
  const [voiceStarted, setVoiceStarted] = useState(false); // in voice mode, wait for explicit "ready"
  const [introTtsLoading, setIntroTtsLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [finishing, setFinishing] = useState(false);

  // Natural transition phrases per language (random pick adds realism)
  const transitionLines = {
    tr: {
      correct: ["Çok güzel. Sonraki soruya geçelim.", "Doğru, hadi devam.", "Harika cevap. Sıradaki soru.", "Mükemmel, bir sonrakine bakalım."],
      wrong: ["Tamam, bir sonraki soruya geçelim.", "Anlaşıldı, devam edelim.", "Hımm, devam ediyoruz.", "Tamam, sıradaki soruya bakalım."],
    },
    en: {
      correct: ["Nice. Let's move on.", "Correct, let's continue.", "Great answer. Next question.", "Excellent, on to the next one."],
      wrong: ["Okay, let's move on.", "Alright, next question.", "Hmm, let's continue.", "I see. Let's go to the next one."],
    },
    es: {
      correct: ["Muy bien. Siguiente pregunta.", "Correcto, continuemos.", "Excelente. Pasemos a la siguiente.", "Genial. La siguiente, por favor."],
      wrong: ["Vale, pasemos a la siguiente.", "Bien, continuemos.", "Entiendo. Siguiente.", "De acuerdo. La próxima pregunta."],
    },
    fr: {
      correct: ["Très bien. Question suivante.", "Correct, continuons.", "Excellent. Passons à la suivante.", "Parfait. La prochaine."],
      wrong: ["D'accord, passons à la suivante.", "Bien, continuons.", "Hmm, on continue.", "Je vois. Question suivante."],
    },
    de: {
      correct: ["Sehr gut. Weiter zur nächsten Frage.", "Richtig, weiter geht's.", "Hervorragend. Nächste Frage.", "Perfekt. Auf zur nächsten."],
      wrong: ["Okay, nächste Frage.", "Gut, machen wir weiter.", "Hmm, weiter geht's.", "Verstanden. Nächste Frage."],
    },
    it: {
      correct: ["Molto bene. Prossima domanda.", "Corretto, continuiamo.", "Ottima risposta. Avanti.", "Perfetto. La prossima."],
      wrong: ["Va bene, passiamo alla prossima.", "Bene, continuiamo.", "Hmm, andiamo avanti.", "Capito. Prossima domanda."],
    },
  };

  const playTransitionAndAdvance = async (isCorrect) => {
    const langKey = (session?.bank_lang || lang || "en");
    const pack = transitionLines[langKey] || transitionLines.en;
    const lines = isCorrect ? pack.correct : pack.wrong;
    const line = lines[Math.floor(Math.random() * lines.length)];
    stopCurrentAudio();
    try {
      const { data } = await api.post("/interview/tts", { text: line, voice, speed });
      const audio = new Audio(`data:${data.mime};base64,${data.audio_base64}`);
      audioPlayerRef.current = audio;
      setCurrentAudioEl(audio);
      audio.onplay = () => setAvatarState("speaking");
      audio.onended = () => {
        setAvatarState("idle");
        goNext();
        // Release the lock — useEffect will now auto-speak the new question
        setTransitioning(false);
      };
      audio.onerror = () => { setAvatarState("idle"); goNext(); setTransitioning(false); };
      audio.play().catch(() => { setAvatarState("idle"); goNext(); setTransitioning(false); });
    } catch (e) {
      goNext();
      setTransitioning(false);
    }
  };

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const autoSpokenForQ = useRef(null);

  // Stop any currently-playing audio so two TTS clips never overlap.
  const stopCurrentAudio = () => {
    const a = audioPlayerRef.current;
    if (a) {
      try {
        a.pause();
        a.currentTime = 0;
      } catch {}
      audioPlayerRef.current = null;
    }
    setCurrentAudioEl(null);
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/interview/sessions/${id}`);
      setSession(data);
    } catch (e) {
      toast.error(t("iv_load_fail"));
      navigate("/app/interview");
    } finally {
      setLoading(false);
    }
  };

  const locked = user && !user.is_admin && (user.plan || "free") === "free";
  useEffect(() => {
    // Reset session-scoped state whenever we navigate to a different session.
    // Otherwise voiceStarted=true from a previous session would hide the
    // "Let's start" button on the resumed session.
    setVoiceStarted(false);
    setReport(null);
    setReportLoading(false);
    setTransitioning(false);
    setAvatarState("idle");
    autoSpokenForQ.current = null;
    stopCurrentAudio();
    if (!locked) load(); else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, locked]);

  // Load detailed report when session is finished
  useEffect(() => {
    if (!session || session.status !== "finished") return;
    if (report || reportLoading) return;
    (async () => {
      setReportLoading(true);
      try {
        const { data } = await api.get(`/interview/sessions/${id}/report`);
        setReport(data);
      } catch (e) {
        // soft fail — basic results still shown
      } finally {
        setReportLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  // Auto-speak question — the AI interviewer should always speak the
  // question out loud so it feels like a real Zoom call, regardless of
  // whether the user picked "voice" or "text" answer mode. In voice
  // mode we still wait for the explicit "Ready?" intro; in text mode
  // we speak straight away.
  useEffect(() => {
    if (!session || !session.current_question) return;
    if (session.mode === "voice" && !voiceStarted) return; // wait for "Başla" button
    if (transitioning) return; // wait until transition audio finishes
    if (muteTTS) return; // user muted the interviewer
    if (autoSpokenForQ.current === session.current_question.id) return;
    autoSpokenForQ.current = session.current_question.id;
    speakQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.current_question?.id, session?.mode, voiceStarted, transitioning, muteTTS]);

  const playIntroAndStart = async () => {
    if (introTtsLoading) return;
    stopCurrentAudio();
    setIntroTtsLoading(true);
    try {
      // Use bank's detected content language for a more realistic, immersive intro
      const introByLang = {
        tr: "Merhaba, ben mülakatçınım. Soruları sırayla soracağım. Cevabını verdikten sonra Gönder'e bas, ben de bir sonrakine geçeyim. Hazır mısın? Başlıyoruz.",
        en: "Hi there, I'll be your interviewer today. I'll ask the questions one by one. After each answer, press Submit and I'll move on. Ready? Let's begin.",
        es: "Hola, soy tu entrevistador. Te haré las preguntas una a una. Después de cada respuesta, pulsa Enviar y pasaré a la siguiente. ¿Listo? Empezamos.",
        fr: "Bonjour, je serai votre intervieweur. Je poserai les questions une par une. Après chaque réponse, appuyez sur Envoyer et je passerai à la suivante. Prêt ? Commençons.",
        de: "Hallo, ich werde Ihr Interviewer sein. Ich stelle die Fragen nacheinander. Drücken Sie nach jeder Antwort auf Senden und ich gehe zur nächsten über. Bereit? Los geht's.",
        it: "Ciao, sarò il tuo intervistatore. Ti farò le domande una alla volta. Dopo ogni risposta, premi Invia e passerò alla successiva. Pronto? Iniziamo.",
      };
      const spokenLang = session?.bank_lang || lang || "en";
      const intro = introByLang[spokenLang] || introByLang.en;
      const { data } = await api.post("/interview/tts", { text: intro, voice, speed });
      const audio = new Audio(`data:${data.mime};base64,${data.audio_base64}`);
      audioPlayerRef.current = audio;
      setCurrentAudioEl(audio);
      audio.onplay = () => setAvatarState("speaking");
      audio.onended = () => {
        setAvatarState("idle");
        setVoiceStarted(true);
      };
      audio.onerror = () => { setAvatarState("idle"); setVoiceStarted(true); };
      audio.play().catch(() => { setAvatarState("idle"); setVoiceStarted(true); });
    } catch (e) {
      setVoiceStarted(true);
    } finally {
      setIntroTtsLoading(false);
    }
  };

  const speakQuestion = async () => {
    if (!session?.current_question) return;
    stopCurrentAudio();
    setTtsLoading(true);
    try {
      const { data } = await api.post("/interview/tts", { text: session.current_question.question, voice, speed });
      const audio = new Audio(`data:${data.mime};base64,${data.audio_base64}`);
      audio.crossOrigin = "anonymous";
      audioPlayerRef.current = audio;
      setCurrentAudioEl(audio);
      audio.onplay = () => setAvatarState("speaking");
      audio.onended = () => setAvatarState("idle");
      audio.onpause = () => setAvatarState((s) => (s === "speaking" ? "idle" : s));
      audio.onerror = () => setAvatarState("idle");
      audio.play().catch(() => setAvatarState("idle"));
    } catch (e) {
      toast.error(t("iv_tts_fail"));
      setAvatarState("idle");
    } finally {
      setTtsLoading(false);
    }
  };

  const startRecording = async () => {
    setFeedback(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setAvatarState("idle");
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("audio", blob, "answer.webm");
          // Language hint — prefer the bank's content language (so an English bank stays English
          // even if UI is Turkish), else fall back to UI language.
          const evalLang = session?.bank_lang || lang;
          if (evalLang) form.append("language", evalLang);
          // Bias whisper with the current question + accepted-answer keywords so it
          // doesn't mishear (e.g. "ABD cumhurbaşkanı" → "İngiltere cumhurbaşkanı").
          const q = session?.current_question?.question;
          if (q) form.append("prompt", q);
          const { data } = await api.post("/voice/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
          setAnswer((data?.text || "").trim());
        } catch (e) {
          toast.error(t("iv_stt_fail"));
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setAvatarState("listening");
    } catch (e) {
      toast.error(t("iv_mic_denied"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const submitAnswer = async () => {
    if (!answer.trim()) {
      toast.error(t("iv_need_answer"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/interview/sessions/${id}/answer`, { answer: answer.trim(), lang });
      setFeedback({
        is_correct: data.is_correct,
        feedback: data.feedback,
        accepted_answers: data.accepted_answers,
      });
      // In voice mode: lock auto-speak until the transition phrase finishes — prevents
      // the new question's TTS from overlapping with the transition.
      const isVoiceContinuing = session?.mode === "voice" && data.status === "active";
      if (isVoiceContinuing) {
        setTransitioning(true);
      }
      // update session counters/state
      setSession((s) => ({
        ...s,
        correct: data.correct,
        wrong: data.wrong,
        current_index: data.current_index,
        status: data.status,
        current_question: data.next_question,
      }));
      if (isVoiceContinuing) {
        setTimeout(() => playTransitionAndAdvance(data.is_correct), 700);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("iv_submit_fail"));
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    setAnswer("");
    setFeedback(null);
  };

  const skipQ = async () => {
    try {
      const { data } = await api.post(`/interview/sessions/${id}/skip`);
      setSession((s) => ({
        ...s,
        wrong: data.wrong,
        correct: data.correct,
        current_index: data.current_index,
        status: data.status,
        current_question: data.next_question,
      }));
      setAnswer("");
      setFeedback(null);
    } catch (e) {
      toast.error(t("iv_skip_fail"));
    }
  };

  const restart = async () => {
    if (!session) return;
    try {
      const { data } = await api.post("/interview/sessions/start", { bank_id: session.bank_id, mode: session.mode });
      navigate(`/app/interview/session/${data.session_id}`, { replace: true });
      // Force reload
      window.location.reload();
    } catch (e) {
      toast.error(t("iv_start_fail"));
    }
  };

  const finishEarly = async () => {
    if (!session) return;
    const attempted = Math.min(session.current_index, session.total);
    const msg = t("iv_finish_early_confirm", { n: attempted, total: session.total });
    if (!window.confirm(msg)) return;
    setFinishing(true);
    stopCurrentAudio();
    setAvatarState("idle");
    try {
      const { data } = await api.post(`/interview/sessions/${id}/finish`);
      setSession((s) => ({
        ...s,
        status: "finished",
        total: data.total ?? s.total,
        correct: data.correct ?? s.correct,
        wrong: data.wrong ?? s.wrong,
        current_question: null,
      }));
      // Clear any in-flight feedback / answer state
      setFeedback(null);
      setAnswer("");
      setTransitioning(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("iv_submit_fail"));
    } finally {
      setFinishing(false);
    }
  };

  if (user && !user.is_admin && (user.plan || "free") === "free") {
    return <InterviewUpgradeGate />;
  }
  if (loading) return <div className="p-8 text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("iv_loading")}</div>;
  if (!session) return null;

  const progress = session.total > 0 ? Math.round(((session.current_index) / session.total) * 100) : 0;
  const finished = session.status === "finished";

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="interview-session-page">
      <Link to={`/app/interview/${session.bank_id}`} className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> {t("iv_back_bank")}
      </Link>

      <div className="mt-4 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{session.bank_name}</div>
          <h1 className="mt-1 text-2xl font-black tracking-tighter">
            {session.mode === "voice" ? t("iv_voice_interview") : t("iv_text_interview")}
          </h1>
        </div>
        <div className="text-sm">
          <span className="text-emerald-600 font-bold">{session.correct} ✓</span>
          <span className="mx-2 text-zinc-400">/</span>
          <span className="text-red-600 font-bold">{session.wrong} ✗</span>
          <span className="mx-2 text-zinc-400">/</span>
          <span className="text-zinc-500">{Math.min(session.current_index, session.total)} of {session.total}</span>
        </div>
      </div>

      {/* progress */}
      <div className="mt-4 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
        <div className="h-full bg-zinc-900 transition-all" style={{ width: `${progress}%` }} />
      </div>

      {/* End-exam-early button: lets the user stop whenever and jump to the report */}
      {!finished && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={finishEarly}
            disabled={finishing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-zinc-300 text-zinc-700 hover:bg-red-50 hover:border-red-300 hover:text-red-700 rounded-md disabled:opacity-60"
            data-testid="finish-early-btn"
            title={t("iv_finish_early")}
          >
            {finishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StopCircle className="w-3.5 h-3.5" />}
            {finishing ? t("iv_finishing") : t("iv_finish_early")}
          </button>
        </div>
      )}

      {/* Avatar: live interviewer character */}
      {!finished && (
        <div className="mt-8 flex justify-center" data-testid="avatar-wrap">
          <InterviewerAvatar state={avatarState} audioEl={currentAudioEl} voice={voice} />
        </div>
      )}

      {/* Voice intro: ask "are you ready?" then user clicks Start */}
      {!finished && session.mode === "voice" && !voiceStarted && (
        <div className="mt-6 border border-zinc-200 bg-white p-6 rounded-md text-center" data-testid="voice-intro">
          <div className="text-sm text-zinc-600">{t("iv_voice_intro_line")}</div>
          <button
            onClick={playIntroAndStart}
            disabled={introTtsLoading}
            className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-bold disabled:opacity-60"
            data-testid="voice-start-btn"
          >
            {introTtsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {t("iv_voice_ready")}
          </button>
        </div>
      )}

      {finished ? (
        <div className="mt-10" data-testid="session-finished">
          {(() => {
            const pct = session.total > 0 ? Math.round((session.correct / session.total) * 100) : 0;
            const tier = pct >= 70 ? "pass" : pct >= 40 ? "ok" : "fail";
            const palette = {
              pass:  { border: "border-emerald-500", bg: "bg-emerald-50", icon: <Trophy className="w-10 h-10 text-emerald-600" />, title: t("iv_congrats"), sub: t("iv_congrats_sub") },
              ok:    { border: "border-amber-500",   bg: "bg-amber-50",   icon: <Sparkles className="w-10 h-10 text-amber-600" />, title: t("iv_good_job"), sub: t("iv_good_job_sub") },
              fail:  { border: "border-red-500",     bg: "bg-red-50",     icon: <Target className="w-10 h-10 text-red-600" />,    title: t("iv_keep_practising"), sub: t("iv_keep_practising_sub") },
            }[tier];
            return (
              <div className={`border-2 ${palette.border} ${palette.bg} p-8 rounded-md text-center`} data-testid={`finish-banner-${tier}`}>
                <div className="flex justify-center">{palette.icon}</div>
                <div className="mt-3 text-2xl sm:text-3xl font-black tracking-tight">{palette.title}</div>
                <div className="mt-1 text-sm text-zinc-700">{palette.sub}</div>
                <div className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("iv_score_label")}</div>
                <h2 className="mt-1 text-5xl font-black">{session.correct} <span className="text-zinc-400">/</span> {session.total}</h2>
                <div className="mt-1 text-zinc-700">
                  <span className="font-bold">{pct}%</span> {t("iv_accuracy")}
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <button onClick={restart} className="btn-accent inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-md" data-testid="restart-btn">
                    <RotateCw className="w-4 h-4" /> {t("iv_restart")}
                  </button>
                  <Link to={`/app/interview/${session.bank_id}`} className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-300 hover:bg-zinc-50 rounded-md font-semibold">
                    {t("iv_back_bank")}
                  </Link>
                </div>
              </div>
            );
          })()}

          {/* Detailed per-question report */}
          <div className="mt-8" data-testid="session-report">
            <h3 className="text-lg font-bold">{t("iv_report_title")}</h3>
            {reportLoading && (
              <div className="mt-3 text-sm text-zinc-500 inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> {t("iv_loading")}
              </div>
            )}
            {report && (
              <ul className="mt-3 space-y-2">
                {report.items.map((item) => (
                  <li
                    key={item.question_id}
                    className={`border rounded-md p-4 ${item.is_correct ? "border-emerald-200 bg-emerald-50/40" : "border-red-200 bg-red-50/40"}`}
                    data-testid={`report-item-${item.question_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center font-bold ${item.is_correct ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
                        {item.is_correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono text-zinc-400">Q{item.index}</div>
                        <div className="mt-0.5 font-semibold">{item.question}</div>
                        {item.min_required > 1 && (
                          <div className="mt-1 text-[11px] text-orange-600 font-semibold uppercase tracking-wider">
                            {t("iv_need_n_answers", { n: item.min_required })}
                          </div>
                        )}
                        <div className="mt-2 text-sm">
                          <span className="text-zinc-500">{t("iv_your_answer")}: </span>
                          <span className={item.user_answer ? "font-medium" : "italic text-zinc-400"}>
                            {item.user_answer || t("iv_skipped")}
                          </span>
                        </div>
                        {!item.is_correct && (
                          <div className="mt-1 text-sm">
                            <span className="text-zinc-500">{t("iv_accepted_were")}: </span>
                            <span className="font-medium">{(item.accepted_answers || []).join(", ")}</span>
                          </div>
                        )}
                        {item.feedback && (
                          <div className="mt-1 text-xs text-zinc-600 italic">{item.feedback}</div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Question */}
          {session.current_question && (
            <div className="mt-8 border border-zinc-900 bg-white p-6 sm:p-8 rounded-md" data-testid="current-question">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-mono text-zinc-400">Q{session.current_index + 1}</div>
                  {hideQuestion ? (
                    <div className="mt-2 text-xl sm:text-2xl font-bold leading-snug text-zinc-300 select-none italic" data-testid="question-hidden">
                      🔊 {t("iv_question_hidden")}
                    </div>
                  ) : (
                    <div className="mt-2 text-xl sm:text-2xl font-bold leading-snug">{session.current_question.question}</div>
                  )}
                  {session.current_question.min_required > 1 && (
                    <div className="mt-2 text-xs text-orange-600 font-semibold uppercase tracking-wider">
                      {t("iv_need_n_answers", { n: session.current_question.min_required })}
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <button
                    onClick={() => setHideQuestion((h) => !h)}
                    className="inline-flex items-center gap-1 px-3 py-2 border border-zinc-300 hover:bg-zinc-50 rounded-md text-sm"
                    data-testid="hide-question-toggle"
                    title={hideQuestion ? t("iv_show_question") : t("iv_hide_question")}
                  >
                    {hideQuestion ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={speakQuestion}
                    disabled={ttsLoading}
                    className="inline-flex items-center gap-1 px-3 py-2 border border-zinc-300 hover:bg-zinc-50 rounded-md text-sm"
                    data-testid="speak-question-btn"
                    title={t("iv_speak")}
                  >
                    {ttsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => {
                      const next = !muteTTS;
                      setMuteTTS(next);
                      localStorage.setItem("iv_mute_tts", next ? "1" : "0");
                      if (next) stopCurrentAudio();
                    }}
                    className={`inline-flex items-center gap-1 px-3 py-2 border rounded-md text-sm ${muteTTS ? "border-orange-400 bg-orange-50 text-orange-700" : "border-zinc-300 hover:bg-zinc-50"}`}
                    data-testid="mute-tts-toggle"
                    title={muteTTS ? (t("iv_unmute_hint") || "Auto-speak off — click to turn on") : (t("iv_mute_hint") || "Auto-speak on — click to mute")}
                  >
                    {muteTTS ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 opacity-70" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Answer area */}
          {!feedback && (
            <div className="mt-4 border border-zinc-200 bg-white p-6 rounded-md" data-testid="answer-block">
              {session.mode === "voice" && (
                <div className="mb-4 flex items-center gap-3">
                  {!recording ? (
                    <button
                      onClick={startRecording}
                      disabled={transcribing || submitting}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md disabled:opacity-60"
                      data-testid="rec-start"
                    >
                      <Mic className="w-4 h-4" /> {t("iv_record")}
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-900 text-white font-semibold rounded-md animate-pulse"
                      data-testid="rec-stop"
                    >
                      <MicOff className="w-4 h-4" /> {t("iv_stop_record")}
                    </button>
                  )}
                  {transcribing && (
                    <span className="text-sm text-zinc-500 inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> {t("iv_transcribing")}
                    </span>
                  )}
                </div>
              )}
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={session.mode === "voice" ? t("iv_voice_answer_ph") : t("iv_text_answer_ph")}
                rows={3}
                className="w-full px-4 py-3 border border-zinc-200 rounded-md focus:outline-none focus:border-blue-600"
                disabled={submitting}
                data-testid="answer-input"
              />
              <div className="mt-3 flex items-center justify-between gap-2">
                <button
                  onClick={skipQ}
                  disabled={submitting}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-md"
                  data-testid="skip-btn"
                >
                  <SkipForward className="w-4 h-4" /> {t("iv_skip")}
                </button>
                <button
                  onClick={submitAnswer}
                  disabled={submitting || !answer.trim()}
                  className="btn-accent inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-md disabled:opacity-60"
                  data-testid="submit-answer-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {t("iv_submit")}
                </button>
              </div>
            </div>
          )}

          {/* Feedback */}
          {feedback && (
            <div className={`mt-4 border-2 p-6 rounded-md ${feedback.is_correct ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50"}`} data-testid="feedback-block">
              <div className="flex items-center gap-2 font-bold">
                {feedback.is_correct ? (
                  <><Check className="w-5 h-5 text-emerald-600" /> <span className="text-emerald-700">{t("iv_correct")}</span></>
                ) : (
                  <><X className="w-5 h-5 text-red-600" /> <span className="text-red-700">{t("iv_wrong")}</span></>
                )}
              </div>
              {feedback.feedback && <div className="mt-2 text-sm text-zinc-700">{feedback.feedback}</div>}
              {!feedback.is_correct && feedback.accepted_answers?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t("iv_accepted_were")}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {feedback.accepted_answers.map((a, i) => (
                      <span key={i} className="inline-block text-xs bg-white border border-zinc-300 px-2 py-0.5 rounded">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4">
                <button
                  onClick={goNext}
                  className="btn-accent inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-md"
                  data-testid="next-q-btn"
                >
                  {session.status === "finished" ? t("iv_see_results") : t("iv_next_question")} →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
